import { useEffect } from 'react'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { SignedIn, SignedOut, RedirectToSignIn, SignIn, SignUp, useAuth } from '@clerk/clerk-react'
import { ToastProvider } from '@orientamos/ui'
import { AppShell } from '@components/layout'
import { ErrorBoundary } from './components/ErrorBoundary'
import { setAuthTokenGetter } from './lib/api'

// Pages
import DashboardPage from '@routes/DashboardPage'
import IntakesPage from '@routes/IntakesPage'
import IntakeNewPage from '@routes/IntakeNewPage'
import IntakeDetailPage from '@routes/IntakeDetailPage'
import IntakeEditPage from '@routes/IntakeEditPage'
import ContractsPage from '@routes/ContractsPage'
import ContractGeneratorPage from '@routes/ContractGeneratorPage'
import ContractDetailPage from '@routes/ContractDetailPage'
import TemplatesPage from '@routes/TemplatesPage'
import TemplateEditorPage from '@routes/TemplateEditorPage'
import ClausesPage from '@routes/ClausesPage'
import SettingsPage from '@routes/SettingsPage'
import NotFoundPage from '@routes/NotFoundPage'

function AuthTokenSync() {
  const { getToken } = useAuth()
  useEffect(() => {
    setAuthTokenGetter(() => getToken())
  }, [getToken])
  return null
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  return (
    <>
      <SignedIn>{children}</SignedIn>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
    </>
  )
}

// Simple fallback for errors outside router context
function ErrorFallback() {
  const handleReload = () => window.location.reload()
  const handleHome = () => (window.location.href = '/')

  return (
    <div className="min-h-screen flex flex-col items-center justify-center text-center px-4 bg-neutral-50">
      <div className="mb-4">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full">
          <svg
            className="w-8 h-8 text-red-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
      </div>
      <h1 className="text-5xl font-bold text-neutral-200 mb-4">500</h1>
      <h2 className="text-2xl font-semibold text-neutral-900 mb-2">Algo salio mal</h2>
      <p className="text-neutral-500 mb-8 max-w-md">Ha ocurrido un error inesperado.</p>
      <div className="flex gap-3">
        <button
          onClick={handleReload}
          className="px-4 py-2 border border-neutral-300 rounded-lg hover:bg-neutral-100"
        >
          Reintentar
        </button>
        <button
          onClick={handleHome}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          Ir al Inicio
        </button>
      </div>
    </div>
  )
}

function App() {
  return (
    <ErrorBoundary fallback={<ErrorFallback />}>
      <ToastProvider>
        <BrowserRouter>
          <Routes>
            {/* Auth routes */}
            <Route
              path="/sign-in/*"
              element={
                <div className="min-h-screen flex items-center justify-center bg-neutral-100">
                  <SignIn routing="path" path="/sign-in" />
                </div>
              }
            />
            <Route
              path="/sign-up/*"
              element={
                <div className="min-h-screen flex items-center justify-center bg-neutral-100">
                  <SignUp routing="path" path="/sign-up" />
                </div>
              }
            />

            {/* Protected routes */}
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <AuthTokenSync />
                  <AppShell>
                    <Routes>
                      <Route path="/" element={<DashboardPage />} />
                      <Route path="/intakes" element={<IntakesPage />} />
                      <Route path="/intakes/new" element={<IntakeNewPage />} />
                      <Route path="/intakes/:id" element={<IntakeDetailPage />} />
                      <Route path="/intakes/:id/edit" element={<IntakeEditPage />} />
                      <Route path="/contracts" element={<ContractsPage />} />
                      <Route path="/contracts/new" element={<ContractGeneratorPage />} />
                      <Route path="/contracts/:id" element={<ContractDetailPage />} />
                      <Route path="/contracts/:id/edit" element={<ContractGeneratorPage />} />
                      <Route path="/templates" element={<TemplatesPage />} />
                      <Route path="/templates/new" element={<TemplateEditorPage />} />
                      <Route path="/templates/:id" element={<TemplateEditorPage />} />
                      <Route path="/templates/:id/edit" element={<TemplateEditorPage />} />
                      <Route path="/clauses" element={<ClausesPage />} />
                      <Route path="/settings" element={<SettingsPage />} />
                      {/* 404 catch-all */}
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  </AppShell>
                </ProtectedRoute>
              }
            />
          </Routes>
        </BrowserRouter>
      </ToastProvider>
    </ErrorBoundary>
  )
}

export default App
